import React from "react";
import { BsArrowLeft } from "react-icons/bs";
import { formatAmount } from "../../utils/formatTime";

import { darkLogo, logo, shareIMG, sharePDF } from "../../assets/images";
import { useUser } from "../../context/user-context";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import CopyToClipboard from "../../components/utils/CopyToClipboard";
import { getNetworkLabel } from "../../constants/constants";
import {
  getTransactionStatusColor,
  shouldMaskFailedAsPending,
} from "../../utils/transactionStatus";
interface ActivityDetailsModalProps {
  onClose: () => void;
  transaction: {
    transactionType: string;
    points?: number;
    source?: string;
    timestamp?: string;
    activityType?: string;
    description?: string;
    message?: string;
    status?: string;
    createdAt?: string;
    processedAt?: string;
    _id: string;
    payoutAmount?: number;
    amount?: number;
    currency?: string;
    from?: {
      amount: number;
      asset: string;
    };
    to?: {
      amount: number;
      asset: string;
    };
    usdtAmount?: number;
    // Crypto sells (transactionType "deex-crypto-sells") come from
    // /api/transactions/me and name their asset with a bare string, unlike the
    // payout shape's `asset` object below.
    sellAsset?: string;
    amountNGN?: number;
    amountUSDT?: number;
    utility?: {
      type: string;
      data: {
        amount: number;
        meter_number?: string;
        phone?: string;
        provider?: string;
        token?: string;
        electricity?: string;
        amount_charged?: string;
        utilityToken?: string;
        tokenValue?: string;
        bonusToken?: string;
        vendType?: string;
        metaData?: {
          disco?: string;
          assetProvider?: string;
          units?: string;
          receiptNo?: number;
          vendTime?: string;
          responseMessage?: string;
          address?: string;
          name?: string;
          phoneNo?: string;
        };
      };
    };
    recipient?: {
      accountNumber: string;
      bankName?: string;
      accountName?: string;
    };
    asset?: {
      amountInUSD: number;
      name?: string;
      amount?: number;
    };
    tradeValue?: Array<{ amount: number }>;
    provider?: {
      name: string;
      data: {
        hash: string;
      };
    };
    txid?: string;
    network?: string;
    toAddress?: string;
    fees?: number;
    quoteID?: string;
  };
}

const calculateGiftcardAmount = (
  transaction: ActivityDetailsModalProps["transaction"]
): number => {
  if (!transaction.tradeValue) return 0;
  return transaction.tradeValue.reduce((total, item) => total + item.amount, 0);
};

const ActivityDetailsModal = ({
  onClose,
  transaction,
}: ActivityDetailsModalProps) => {
  const { theme } = useUser();
  const receiptRef = React.useRef<HTMLDivElement>(null);

  const forceLightTheme = () => {
    if (!receiptRef.current) return;

    receiptRef.current.classList.add("force-light");

    // Swap logos to light version
    const logos = receiptRef.current.querySelectorAll("img");
    logos.forEach((img) => {
      if (img.getAttribute("src")?.includes("logo")) {
        img.setAttribute("data-original-src", img.getAttribute("src") || "");
        img.setAttribute("src", logo);
      }
    });
  };

  const revertTheme = () => {
    if (!receiptRef.current) return;

    receiptRef.current.classList.remove("force-light");

    const logos = receiptRef.current.querySelectorAll("img");
    logos.forEach((img) => {
      const originalSrc = img.getAttribute("data-original-src");
      if (originalSrc) {
        img.setAttribute("src", originalSrc);
        img.removeAttribute("data-original-src");
      }
    });
  };

  const handleShareAsPDF = async () => {
    if (receiptRef.current) {
      forceLightTheme(); // force light before screenshot

      const canvas = await html2canvas(receiptRef.current);
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save("transaction-receipt.pdf");

      revertTheme(); // revert after
    }
  };

  const handleShareAsImage = async () => {
    if (receiptRef.current) {
      forceLightTheme(); // force light before screenshot

      const canvas = await html2canvas(receiptRef.current);
      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = `transaction-receipt-${transaction?._id}.png`;
      link.click();

      revertTheme(); // revert after
    }
  };

  function formatCustomDate(isoDate: string): string {
    const date = new Date(isoDate);

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const getOrdinal = (n: number): string => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };

    const month = months[date.getMonth()];
    const day = date.getDate();
    const ordinal = getOrdinal(day);
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    // const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 => 12

    return `${month} ${day}${ordinal}, ${year}. ${hours}:${minutes} ${ampm}`;
  }

  const getExplorerTxUrl = (network?: string, hash?: string): string | null => {
    if (!hash) return null;
    const net = (network || "").toLowerCase();
    if (
      net.includes("tron") ||
      net.includes("trc") ||
      net.includes("trx") ||
      net.includes("trc20") ||
      net.includes("trc-20")
    ) {
      return `https://tronscan.org/#/transaction/${hash}`;
    }
    if (
      net.includes("bsc") ||
      net.includes("binance") ||
      net.includes("bnb") ||
      net.includes("bep20") ||
      net.includes("bep-20")
    ) {
      return `https://bscscan.com/tx/${hash}`;
    }
    if (net.includes("polygon") || net.includes("matic")) {
      return `https://polygonscan.com/tx/${hash}`;
    }
    if (net.includes("arbitrum")) {
      return `https://arbiscan.io/tx/${hash}`;
    }
    if (net.includes("optimism") || net.includes("optimistic")) {
      return `https://optimistic.etherscan.io/tx/${hash}`;
    }
    if (net.includes("avalanche") || net.includes("avax")) {
      return `https://snowtrace.io/tx/${hash}`;
    }
    if (net.includes("fantom")) {
      return `https://ftmscan.com/tx/${hash}`;
    }
    if (net.includes("base")) {
      return `https://basescan.org/tx/${hash}`;
    }
    if (net.includes("linea")) {
      return `https://lineascan.build/tx/${hash}`;
    }
    if (net.includes("zksync") || net.includes("zk-sync")) {
      return `https://explorer.zksync.io/tx/${hash}`;
    }
    if (net.includes("solana") || net.includes("sol")) {
      return `https://solscan.io/tx/${hash}`;
    }
    if (net.includes("btc") || net.includes("bitcoin")) {
      return `https://btcscan.org/tx/${hash}`;
    }
    if (net.includes("doge") || net.includes("dogecoin")) {
      return `https://dogechain.info/tx/${hash}`;
    }
    if (
      net.includes("eth") ||
      net.includes("ethereum") ||
      net.includes("erc20") ||
      net.includes("erc-20")
    ) {
      return `https://etherscan.io/tx/${hash}`;
    }
    if (
      net.includes("shib") ||
      net.includes("shibcoin") ||
      net.includes("shib-coin")
    ) {
      return `https://shibascan.io/tx/${hash}`;
    }
    if (net.includes("sol") || net.includes("solana")) {
      return `https://solscan.io/tx/${hash}`;
    }
    // default to etherscan
    return `https://etherscan.io/tx/${hash}`;
  };

  // console.log(transaction);

  return (
    <div className="fixed inset-0 z-50 font-manrope flex justify-center items-start pt-16 overflow-auto pb-16 bg-black bg-opacity-75">
      <div className="bg-gray-100 dark:bg-[#1C2227] rounded-xl w-11/12 max-w-[500px] relative">
        <div className="p-6">
          <div className="w-full flex justify-between  items-center">
            <button onClick={onClose}>
              <BsArrowLeft className="text-black dark:text-white text-[20px]" />
            </button>
            <h4 className="text-gray-900 dark:text-gray-100 font-sora font-bold text-[20px]">
              Receipt
            </h4>
            <div></div>
          </div>

          <div
            ref={receiptRef}
            className=" mt-8 bg-white rounded-lg pt-4  bg-receiptBg bg-cover dark:bg-[#00000066]"
          >
            <div className="flex flex-col mt-6 items-center">
              {theme === "light" ? (
                <img src={darkLogo} className="w-[135px] h-[35px]" alt="logo" />
              ) : (
                <img src={logo} className="w-[135px] h-[35px]" alt="logo" />
              )}
              <div
                className={`font-bold mt-4 text-[13px] ${getTransactionStatusColor(
                  transaction.status,
                  transaction
                )}`}
              >
                {(transaction?.transactionType === "deex-asset-swaps" &&
                  !transaction?.status) ||
                transaction?.transactionType === "crypto-deposit" ||
                transaction?.transactionType === "deex-deposits" ||
                (transaction?.transactionType === "deex-crypto-withdrawals" &&
                  !transaction?.utility &&
                  !transaction?.status)
                  ? "COMPLETED"
                  : shouldMaskFailedAsPending(transaction)
                  ? "PENDING"
                  : transaction?.status}
              </div>
              <h4 className="font-sora font-bold text-[22px] text-gray-900 dark:text-gray-100 mt-2 mb-6">
                {transaction.transactionType === "payouts"
                  ? `${formatAmount(
                      transaction?.asset?.amount || 0
                    )} ${transaction?.asset?.name?.toUpperCase()}`
                  : transaction.transactionType === "gift-card-transactions"
                  ? `$${formatAmount(
                      transaction?.tradeValue?.reduce(
                        (total: any, item: { amount: any }) =>
                          total + item.amount,
                        0
                      )
                    )}`
                  : transaction.utility &&
                    transaction.amount &&
                    transaction.currency
                  ? `${formatAmount(
                      transaction.amount
                    )} ${transaction.currency.toUpperCase()}`
                  : transaction.transactionType === "deex-crypto-withdrawals" &&
                    transaction.amount &&
                    transaction.currency
                  ? `${formatAmount(
                      transaction.amount
                    )} ${transaction.currency.toUpperCase()}`
                  : transaction.transactionType === "deex-asset-swaps" &&
                    transaction.from
                  ? `${formatAmount(transaction.from.amount)} ${
                      transaction.from.asset
                    }`
                  : transaction.transactionType === "crypto-deposit" &&
                    transaction.usdtAmount &&
                    transaction.currency
                  ? `${formatAmount(
                      transaction.usdtAmount
                    )} ${transaction.currency.toUpperCase()}`
                  : transaction.transactionType === "deex-fiat-deposits" &&
                    transaction.amount
                  ? `₦${formatAmount(transaction.amount)}`
                  : transaction.transactionType === "deex-deposits" &&
                    transaction.amount &&
                    transaction.currency
                  ? `${formatAmount(
                      transaction.amount
                    )} ${transaction.currency.toUpperCase()}`
                  : // Crypto sells come from /api/transactions/me and carry
                  // `asset` rather than `currency`.
                  transaction.transactionType === "deex-crypto-sells" &&
                    transaction.amount &&
                    transaction.sellAsset
                  ? `${formatAmount(
                      transaction.amount
                    )} ${transaction.sellAsset.toUpperCase()}`
                  : ""}
              </h4>
            </div>
            {/* Transaction Type */}
            <div className="flex justify-between mt-4 px-4 items-start gap-2  ">
              <span className="text-gray-500 dark:text-gray-400 text-sm">
                Transaction Type
              </span>
              <span className="text-gray-900 dark:text-gray-100 text-[13px] font-semibold">
                {transaction.transactionType
                  .split("-")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() + word.slice(1)
                  )
                  .join(" ")}
              </span>
            </div>

            {/* Amount */}
            <div className="flex justify-between mt-4 px-4 flex-row items-start gap-2 ">
              <span className="text-gray-500 dark:text-gray-400 text-sm">
                Amount
              </span>
              <span className="text-gray-900 dark:text-gray-100 text-[13px] font-semibold">
                {transaction.transactionType === "payouts" ||
                transaction.transactionType === "gift-card-transactions"
                  ? `₦${formatAmount(
                      transaction.payoutAmount ||
                        calculateGiftcardAmount(transaction)
                    )}`
                  : transaction.utility &&
                    transaction.amount &&
                    transaction.currency
                  ? `${formatAmount(
                      transaction.amount
                    )} ${transaction.currency.toUpperCase()}`
                  : transaction.transactionType === "deex-crypto-withdrawals" &&
                    transaction.amount &&
                    transaction.currency
                  ? `${formatAmount(
                      transaction.amount
                    )} ${transaction.currency.toUpperCase()}`
                  : transaction.transactionType === "deex-asset-swaps" &&
                    transaction.from
                  ? `${formatAmount(transaction.from.amount)} ${
                      transaction.from.asset
                    }`
                  : transaction.transactionType === "crypto-deposit" &&
                    transaction.usdtAmount &&
                    transaction.currency
                  ? `${formatAmount(
                      transaction.usdtAmount
                    )} ${transaction.currency.toUpperCase()}`
                  : transaction.transactionType === "deex-fiat-deposits" &&
                    transaction.amount
                  ? `₦${formatAmount(transaction.amount)}`
                  : transaction.transactionType === "deex-deposits" &&
                    transaction.amount &&
                    transaction.currency
                  ? `${formatAmount(
                      transaction.amount
                    )} ${transaction.currency.toUpperCase()}`
                  : transaction.transactionType === "deex-crypto-sells" &&
                    transaction.amount &&
                    transaction.sellAsset
                  ? `${formatAmount(
                      transaction.amount
                    )} ${transaction.sellAsset.toUpperCase()}`
                  : ""}
              </span>
            </div>

            {/* Secondary Amount (if applicable) */}
            {(transaction.transactionType === "payouts" &&
              transaction.asset?.amountInUSD) ||
            (transaction.transactionType === "gift-card-transactions" &&
              transaction.tradeValue) ||
            (transaction.utility && transaction.utility.data.amount) ||
            (transaction.transactionType === "deex-asset-swaps" &&
              transaction.to) ||
            (transaction.transactionType === "deex-crypto-sells" &&
              (transaction.amountNGN || transaction.amountUSDT)) ? (
              <div className="flex justify-between mt-4 px-4 flex-row items-start gap-2  ">
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  {transaction.transactionType === "payouts"
                    ? "USD Value"
                    : transaction.transactionType === "gift-card-transactions"
                    ? "Trade Value"
                    : transaction.utility
                    ? "Utility Amount"
                    : transaction.transactionType === "deex-crypto-sells"
                    ? "Amount sold"
                    : "Swap To"}
                </span>
                <span className="text-gray-900 dark:text-gray-100 text-[13px] font-semibold">
                  {transaction.transactionType === "payouts" &&
                  transaction.asset
                    ? `$${formatAmount(transaction.asset.amountInUSD)}`
                    : transaction.transactionType ===
                        "gift-card-transactions" && transaction.tradeValue
                    ? `$${formatAmount(
                        transaction?.tradeValue?.reduce(
                          (total: any, item: { amount: any }) =>
                            total + item.amount,
                          0
                        )
                      )}`
                    : transaction.utility &&
                      transaction.utility.type === "ELECTRICITY" &&
                      transaction.utility.data.amount_charged
                    ? `₦${formatAmount(
                        parseInt(
                          transaction.utility.data.amount_charged.replace(
                            /[^\d]/g,
                            ""
                          )
                        )
                      )}`
                    : transaction.utility
                    ? `₦${formatAmount(
                        Number(transaction.utility.data.amount)
                      )}`
                    : transaction.transactionType === "deex-asset-swaps" &&
                      transaction.to
                    ? `${formatAmount(transaction.to.amount)} ${
                        transaction.to.asset
                      }`
                    : transaction.transactionType === "deex-crypto-sells"
                    ? transaction.amountNGN
                      ? `₦${formatAmount(transaction.amountNGN)}`
                      : `$${formatAmount(transaction.amountUSDT)}`
                    : ""}
                </span>
              </div>
            ) : null}

            {transaction.network && (
              <div className="flex flex-col">
                <div className="flex justify-between px-4 mt-4 flex-row items-start gap-6  ">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Network
                  </span>
                  <span className="text-gray-900 dark:text-gray-100 font-semibold text-[13px]">
                    {transaction.network
                      ? getNetworkLabel(transaction.network)
                      : ""}
                  </span>
                </div>
              </div>
            )}

            {transaction.transactionType === "payouts" && (
              <>
                <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Cash Destination
                  </span>
                  <span
                    className={`font-semibold text-[13px] text-gray-900 dark:text-gray-100`}
                  >
                    {transaction?.recipient?.accountNumber}
                  </span>
                </div>
                <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Bank Name
                  </span>
                  <span
                    className={`font-semibold text-[13px]  text-gray-900 dark:text-gray-100`}
                  >
                    {transaction?.recipient?.bankName ?? "N/A"}
                  </span>
                </div>
                <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Acct Name
                  </span>
                  <span
                    className={`font-semibold text-[13px] text-gray-900 dark:text-gray-100`}
                  >
                    {transaction?.recipient?.accountName ?? "N/A"}
                  </span>
                </div>
              </>
            )}

            {/* Transaction Date */}
            <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
              <span className="text-gray-500 dark:text-gray-400 text-sm">
                Timestamp
              </span>
              <span className="text-gray-900 dark:text-gray-100 text-sm">
                {formatCustomDate(
                  transaction.createdAt || transaction.processedAt || ""
                )}
              </span>
            </div>
            {typeof transaction.fees === "number" && (
              <div className="flex justify-between px-4 mt-4 flex-row items-start gap-6">
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  Fee
                </span>
                <div className="text-gray-900 dark:text-gray-100 text-sm font-semibold break-all max-w-full text-right">
                  {formatAmount(transaction.fees)}{" "}
                  {transaction?.currency?.toUpperCase()}
                </div>
              </div>
            )}

            {/* Transaction ID */}
            <div className="flex justify-between  px-4 mt-4 flex-row items-start gap-2  ">
              <span className="text-gray-500 dark:text-gray-400 text-sm">
                Transaction ID
              </span>
              <CopyToClipboard text={transaction._id}>
                <span className="text-secondary_button text-sm font-mono cursor-pointer">
                  {transaction._id}
                </span>
              </CopyToClipboard>
            </div>
            {transaction.toAddress && (
              <div className="flex justify-between  px-4 mt-4 flex-row items-start gap-6  ">
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  {transaction.transactionType === "deex-crypto-withdrawals"
                    ? "Withdrawn To"
                    : transaction.transactionType === "deex-deposits"
                    ? "Deposited To"
                    : ""}
                </span>
                <CopyToClipboard text={transaction.toAddress}>
                  <div className="text-secondary_button text-sm font-mono cursor-pointer break-all max-w-full text-right">
                    {transaction.toAddress}
                  </div>
                </CopyToClipboard>
              </div>
            )}
            {transaction.quoteID && (
              <div className="flex justify-between  px-4 mt-4 flex-row items-start gap-6  ">
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  Quote ID
                </span>
                <CopyToClipboard text={transaction.quoteID}>
                  <div className="text-secondary_button text-sm font-mono cursor-pointer break-all max-w-full text-right">
                    {transaction.quoteID}
                  </div>
                </CopyToClipboard>
              </div>
            )}

            {transaction.provider && (
              <div className="flex flex-col">
                {transaction?.provider?.data?.hash && (
                  <div className="flex justify-between px-4 mt-4 flex-row items-start gap-6  ">
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                      Transaction Hash
                    </span>
                    <div className="text-right max-w-full">
                      <a
                        href={
                          getExplorerTxUrl(
                            transaction?.network,
                            transaction?.provider?.data?.hash
                          ) || "#"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondary_button text-sm font-mono break-all max-w-full hover:underline"
                      >
                        {transaction.provider?.data?.hash || ""}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Additional Details based on transaction type */}
            {transaction.utility && (
              <>
                {/* Utility Type */}
                <div className="flex justify-between px-4  mt-4 flex-row items-start gap-2  ">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Utility Type
                  </span>
                  <span className="text-gray-900 dark:text-gray-100 font-semibold">
                    {transaction.utility.type}
                  </span>
                </div>

                {/* Electricity Details */}
                {transaction.utility.type === "ELECTRICITY" &&
                  (transaction.utility.data?.metaData?.disco ||
                    transaction.utility.data?.electricity) && (
                    <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        Provider
                      </span>
                      <span className="text-gray-900 dark:text-gray-100 font-semibold">
                        {transaction.utility.data?.metaData?.disco ??
                          transaction.utility.data?.metaData?.assetProvider ??
                          transaction.utility.data?.electricity}
                      </span>
                    </div>
                  )}

                {transaction.utility.type === "ELECTRICITY" &&
                  transaction.utility.data?.meter_number && (
                    <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        Meter Number
                      </span>
                      <span className="text-gray-900 dark:text-gray-100 font-semibold">
                        {transaction.utility.data.meter_number}
                      </span>
                    </div>
                  )}

                {transaction.utility.type === "ELECTRICITY" &&
                  transaction.utility.data?.vendType && (
                    <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        Vend Type
                      </span>
                      <span className="text-gray-900 dark:text-gray-100 font-semibold">
                        {transaction.utility.data.vendType}
                      </span>
                    </div>
                  )}

                {transaction.utility.type === "ELECTRICITY" &&
                  (transaction.utility.data?.utilityToken ||
                    transaction.utility.data?.token) && (
                    <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        Utility Token
                      </span>
                      <CopyToClipboard
                        text={(
                          transaction.utility.data?.utilityToken ??
                          transaction.utility.data?.token ??
                          ""
                        )
                          .toString()
                          .replace("Token : ", "")}
                      >
                        <span className="text-secondary_button text-sm font-mono cursor-pointer break-all">
                          {(
                            transaction.utility.data?.utilityToken ??
                            transaction.utility.data?.token ??
                            ""
                          )
                            .toString()
                            .replace("Token : ", "")}
                        </span>
                      </CopyToClipboard>
                    </div>
                  )}

                {transaction.utility.type === "ELECTRICITY" &&
                  (transaction.utility.data?.tokenValue ||
                    transaction.utility.data?.metaData?.units) && (
                    <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        Units (kWh)
                      </span>
                      <span className="text-gray-900 dark:text-gray-100 font-semibold">
                        {transaction.utility.data?.tokenValue ??
                          transaction.utility.data?.metaData?.units}
                      </span>
                    </div>
                  )}

                {(transaction.utility.type === "AIRTIME" ||
                  transaction.utility.type === "DATA") &&
                  transaction.utility.data.phone && (
                    <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        Phone Number
                      </span>
                      <span className="text-gray-900 dark:text-gray-100 font-semibold">
                        {transaction.utility.data.phone}
                      </span>
                    </div>
                  )}

                {transaction.utility.type === "BETTING" &&
                  transaction.utility.data.provider && (
                    <div className="flex justify-between px-4 mt-4 flex-row items-start gap-2  ">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        Provider
                      </span>
                      <span className="text-gray-900 dark:text-gray-100 font-semibold">
                        {transaction.utility.data.provider.toUpperCase()}
                      </span>
                    </div>
                  )}
              </>
            )}
            <div className="w-11/12 mx-auto border  mt-4 border-dashed "></div>
            <div className="w-11/12 mx-auto px-4 mt-4 text-[11px] text-gray-500 dark:text-gray-400">
              Join the DeeX today and start earning rewards. Use crypto to
              settle bill payments, instant payouts, and take control of your
              financial future! Sign up now and discover the power of crypto!
            </div>
            <div className="w-full flex justify-start  items-center mt-4">
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
              <div className=" w-[24px] rounded-full bg-gray-100 h-[24px] top-2 relative dark:bg-[#1C2227]" />
            </div>
          </div>
          <div className="w-full mt-8 gap-4 flex items-center">
            <button
              onClick={handleShareAsPDF}
              className="w-full  font-manrope gap-2 flex justify-center items-center border border-secondary_button  text-secondary_button py-4 rounded-xl font-medium"
            >
              <div className="h-[24px] w-[24px]">
                <img
                  src={sharePDF}
                  className="w-full h-full bg-cover object-contain"
                  alt=""
                />
              </div>
              Share as PDF
            </button>
            <button
              onClick={handleShareAsImage}
              className="w-full gap-2 font-manrope flex justify-center border border-secondary_button items-center text-secondary_button py-4 rounded-xl font-medium"
            >
              <div className="h-[24px] w-[24px]">
                <img
                  src={shareIMG}
                  className="w-full h-full bg-cover object-contain"
                  alt=""
                />
              </div>
              Share as Image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetailsModal;
